import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import KeepLink from '../components/KeepLink';
import CorpusGraph from '../components/CorpusGraph';
import HomeDivergencePanel from '../components/HomeDivergencePanel';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import type { PaalSlug } from '../data/types';
import './Home.css';

const PAAL_ORDER: PaalSlug[] = ['aram', 'porul', 'inbam'];

/**
 * Deterministic kural-of-the-day: (day-of-year UTC) % 1330 + 1.
 * Stable per day without any backend.
 */
function kuralOfTheDay(now: Date = new Date()): number {
    const start = Date.UTC(now.getUTCFullYear(), 0, 0);
    const diff = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
    ) - start;
    const dayOfYear = Math.floor(diff / 86_400_000);
    return (dayOfYear % 1330) + 1;
}

export default function Home() {
    const graph = useGraphIndex();
    const [lang] = useLang();
    const navigate = useNavigate();
    const { search } = useLocation();
    const [randomSeed, setRandomSeed] = useState(0);

    const todayN = useMemo(() => kuralOfTheDay(), []);
    const kotd = graph.data?.kurals[todayN - 1];

    const paalTiles = useMemo(() => {
        if (!graph.data) return [];
        return PAAL_ORDER.map((slug) => {
            const p = graph.data!.paals.find((x) => x.slug === slug);
            if (!p) return null;
            const firstN = p.adhigaram_ns[0]
                ? graph.data!.adhigarams.find((a) => a.n === p.adhigaram_ns[0])
                    ?.kural_ns[0] ?? 1
                : 1;
            return { paal: p, firstN };
        }).filter((x): x is { paal: NonNullable<typeof x>['paal']; firstN: number } => x !== null);
    }, [graph.data]);

    const goRandom = useCallback(() => {
        // useState just to trigger re-render on repeated clicks; the roll
        // itself is Math.random.
        const n = Math.floor(Math.random() * 1330) + 1;
        setRandomSeed((s) => s + 1);
        navigate(`/kural/${n}${search}`);
    }, [navigate, search]);
    void randomSeed;

    if (graph.error) {
        return (
            <div className="focus-error" role="alert">
                <p>Sorry — we couldn't load the corpus index. Refresh to try again.</p>
            </div>
        );
    }
    if (graph.loading || !graph.data) {
        return (
            <div className="focus-loading" aria-live="polite">
                <span className="focus-loading__dot" aria-hidden="true" />
                <span>Loading…</span>
            </div>
        );
    }

    return (
        <div className="home">
            <header className="home__hero-head">
                <h1 className="sr-only">Meiporul — a semantic map of the Thirukkural</h1>
                <blockquote className="home__epigraph">
                    <p lang="ta" className="home__epigraph-line">
                        எப்பொருள் யார்யார்வாய்க் கேட்பினும் அப்பொருள்
                    </p>
                    <p lang="ta" className="home__epigraph-line">
                        மெய்ப்பொருள் காண்ப தறிவு
                    </p>
                    <cite lang="ta" className="home__epigraph-cite">குறள் 423</cite>
                </blockquote>
                {lang === 'en' && (
                    <p className="home__hero-translation">
                        <em>Whatever is said, whoever says it, wisdom is seeing through to what is true.</em>
                    </p>
                )}
                <p className="home__hero-tagline" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                    {lang === 'ta' ? (
                        <>
                            ஈராயிரம் ஆண்டுகள் கடந்த வாக்கு —<br />
                            இன்று உயிர்பெறும் வரைபடம்.
                        </>
                    ) : (
                        'Two thousand years old. Now a living map.'
                    )}
                </p>
                <p className="home__hero-lede" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                    {lang === 'ta'
                        ? 'ஆயிரத்து முந்நூற்று முப்பது குறள்கள், அவற்றின் கருத்துக் கோலங்கள், வரிகளுக்கிடையே மறைந்து கிடக்கும் நுண்ணிய இழைகள் — யாவும் நுண்ணறிவின் தேடலால் வரையப்பட்ட ஒரு வரைபடம்.'
                        : "An interactive semantic map of the Thirukkural: 1,330 verses, their themes, and the hidden threads between them, traced with generative AI's reasoning."}
                </p>
                <p className="home__hero-signature" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                    {lang === 'ta' ? 'ஒரு தனிநபரின் ஆர்வத் திட்டம் ' : 'A solo passion project '}
                    <span aria-hidden="true">·</span>{' '}
                    <KeepLink to="/about" className="home__hero-about">
                        {lang === 'ta' ? 'விவரம் →' : 'About →'}
                    </KeepLink>
                </p>
                <p className="home__hero-mobile-note" role="note" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                    {lang === 'ta'
                        ? 'மடிக்கணினி அல்லது டெஸ்க்டாப்பில் சிறப்பாகக் காணலாம் — வரைபடத்திற்கு இடம் தேவை.'
                        : 'Best viewed on a laptop or desktop — the map likes room to breathe.'}
                </p>
            </header>

            <section
                className="home__hero"
                aria-label={lang === 'ta' ? 'கருப்பொருள் வரைபடம்' : 'Thematic corpus graph'}
            >
                <CorpusGraph />
            </section>

            <div className="home__secondary">
                {kotd && (
                    <section className="home__kotd" aria-labelledby="kotd-heading">
                        <div className="home__kotd-eyebrow" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                            <span id="kotd-heading">
                                {lang === 'ta' ? 'இன்றைய குறள்' : 'Kural of the day'}
                            </span>
                            <span aria-hidden="true"> · </span>
                            <span>{lang === 'ta' ? `குறள் ${kotd.n}` : `Kural ${kotd.n}`}</span>
                        </div>
                        <KeepLink to={`/kural/${kotd.n}`} className="home__kotd-body">
                            {lang === 'ta' ? (
                                <>
                                    <div lang="ta" className="home__kotd-verse">
                                        {kotd.verse_ta.map((line, i) => (
                                            <span key={i} className="home__kotd-verse-line">
                                                {line}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="home__kotd-gist">{kotd.gist_en}</div>
                                </>
                            ) : (
                                <>
                                    <div className="home__kotd-gist home__kotd-gist--lead">
                                        {kotd.gist_en}
                                    </div>
                                    <div lang="ta" className="home__kotd-verse home__kotd-verse--secondary">
                                        {kotd.verse_ta.map((line, i) => (
                                            <span key={i} className="home__kotd-verse-line">
                                                {line}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            )}
                            <div className="home__kotd-meta">
                                {lang === 'ta' ? (
                                    <span lang="ta">{kotd.adhigaram_title_ta}</span>
                                ) : (
                                    kotd.adhigaram_title_en
                                )}
                            </div>
                        </KeepLink>
                    </section>
                )}

                <section
                    className="home__side"
                    aria-label={lang === 'ta' ? 'ஆராய்தல்' : 'Explore'}
                >
                    <div className="home__side-group">
                        <h2 className="home__side-title" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                            {lang === 'ta' ? 'பால்கள்' : 'Paals'}
                        </h2>
                        <ul className="home__paal-tiles">
                            {paalTiles.map(({ paal, firstN }) => (
                                <li key={paal.slug}>
                                    <KeepLink
                                        to={`/kural/${firstN}`}
                                        className={`home__paal-tile home__paal-tile--${paal.slug}`}
                                    >
                                        {lang === 'ta' ? (
                                            <>
                                                <span lang="ta" className="home__paal-ta">
                                                    {paal.title_ta}
                                                </span>
                                                <span className="home__paal-en">
                                                    {paal.title_en}
                                                </span>
                                                <span lang="ta" className="home__paal-count">
                                                    {paal.adhigaram_ns.length} அதிகாரங்கள்
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="home__paal-en home__paal-en--lead">
                                                    {paal.title_en}
                                                </span>
                                                <span lang="ta" className="home__paal-ta home__paal-ta--secondary">
                                                    {paal.title_ta}
                                                </span>
                                                <span className="home__paal-count">
                                                    {paal.adhigaram_ns.length} adhigarams
                                                </span>
                                            </>
                                        )}
                                    </KeepLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="home__side-group">
                        <button
                            type="button"
                            onClick={goRandom}
                            className="home__random"
                            aria-label={lang === 'ta' ? 'ஏதேனும் ஒரு குறளைத் திற' : 'Open a random kural'}
                            {...(lang === 'ta' ? { lang: 'ta' } : {})}
                        >
                            <span aria-hidden="true">↻</span>{' '}
                            {lang === 'ta' ? 'ஏதேனும் ஒரு குறள்' : 'Random kural'}
                        </button>
                        <p className="home__random-hint" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                            {lang === 'ta'
                                ? 'அல்லது எங்கும் "/" ஐ அழுத்தித் தேடலாம்.'
                                : 'Or press "/" anywhere to search.'}
                        </p>
                    </div>
                </section>
            </div>

            <HomeDivergencePanel lang={lang} />

            <details className="home__themes" open>
                <summary className="home__themes-summary" {...(lang === 'ta' ? { lang: 'ta' } : {})}>
                    {lang === 'ta' ? 'அல்லது கருப்பொருள் வாரியாகப் பார்வையிடலாம் ' : 'Or browse by theme '}
                    <span className="home__themes-count">({graph.data.themes.length})</span>
                </summary>
                <ul className="home__theme-strip">
                    {graph.data.themes.map((t) => (
                        <li key={t.slug}>
                            <KeepLink
                                to={`/theme/${t.slug}`}
                                className="home__theme-chip"
                            >
                                {lang === 'ta' ? (
                                    <>
                                        <span lang="ta">{t.title_ta}</span>
                                        <span className="home__theme-sub">
                                            {t.title_en}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span>{t.title_en}</span>
                                        <span lang="ta" className="home__theme-sub">
                                            {t.title_ta}
                                        </span>
                                    </>
                                )}
                            </KeepLink>
                        </li>
                    ))}
                </ul>
            </details>

            <section
                className="home__thanks"
                aria-labelledby="home-thanks-heading"
            >
                <h2
                    id="home-thanks-heading"
                    className="home__thanks-title"
                    {...(lang === 'ta' ? { lang: 'ta' } : {})}
                >
                    {lang === 'ta' ? 'நன்றியுடன்' : 'Special thanks to'}
                </h2>
                {lang === 'ta' ? (
                    <ul className="home__thanks-list" lang="ta">
                        <li>
                            <strong>Project Madurai &amp; Tamil Virtual Academy</strong>{' '}
                            — பல்லாண்டுக் காலம் பொறுமையாய், ஓலைச்சுவடியில் இருந்த தமிழை எழுத்துருவில் நிலைநிறுத்தி, வருங்காலத்திற்குக் காத்துத் தந்தமைக்கு.
                        </li>
                        <li>
                            <strong>Google Research</strong> — இச்சிந்தனையே சாத்தியம் என்றாக்கிய அடித்தள கட்டமைப்பை உலகுக்கு அளித்தமைக்கு.
                        </li>
                        <li>
                            <strong>Anthropic&rsquo;s Claude</strong> — இத்தளத்தை உருவாக்கிய குறியீட்டு உரையாடல்களுக்கும், ஒவ்வொரு கருத்திழையையும் இனங்கண்டு வகைப்படுத்திய நுண்ணறிவுக்கும்.
                        </li>
                    </ul>
                ) : (
                    <ul className="home__thanks-list">
                        <li>
                            <strong>Project Madurai</strong> and the{' '}
                            <strong>Tamil Virtual Academy</strong> — for spending
                            decades putting Tamil literature into Unicode.
                        </li>
                        <li>
                            <strong>Google Research</strong> — for the transformer
                            architecture that made this thinkable.
                        </li>
                        <li>
                            <strong>Anthropic's Claude</strong> — for the coding
                            conversations that built this site, and the reasoning
                            that tagged every theme.
                        </li>
                    </ul>
                )}
            </section>
        </div>
    );
}
